/**
 * THE model price table — USD per 1,000,000 tokens. One table for the whole
 * repo: the Custom Bid Tabs extraction ledger and `@edgevanta/ai`'s token-usage
 * accounting both read it.
 *
 * It lives in this package because the dependency only runs one way: `ai`
 * depends on `bid-tab-extract`, so `ai` imports from here and there is no cycle.
 * It is NOT duplicated on the other side — two tables is how `gpt-4.1-mini`
 * ended up priced at 0.40/1.60 here and 0.50/1.50 there, and how a model can be
 * callable by one ledger and unknown to the other.
 *
 * An unknown model returns null so no ledger ever records a fabricated number.
 */

/**
 * USD per 1M tokens in each disjoint provider-usage bucket.
 *
 * The cache buckets are OPTIONAL because not every entry has a published, known
 * cache price — an entry may honestly carry only what we know. A call that DOES
 * report cache tokens against a model with no cache price prices to null rather
 * than to a guessed number.
 */
export interface ModelPrice {
  input: number;
  output: number;
  cacheWrite?: number;
  cacheRead?: number;
}

const SONNET_5_STANDARD_PRICE_AT_MS = Date.parse('2026-09-01T00:00:00.000Z');
const SONNET_5_STANDARD_PRICE: ModelPrice = {
  input: 3.0,
  output: 15.0,
  cacheWrite: 3.75,
  cacheRead: 0.3,
};

/**
 * Models are keyed by the exact id passed to the provider SDK. Ids are unique
 * across providers, so no provider dimension is needed to look one up.
 */
export const MODEL_PRICING: Record<string, ModelPrice> = {
  'claude-sonnet-5': {
    input: 2.0,
    output: 10.0,
    cacheWrite: 2.5,
    cacheRead: 0.2,
  },
  'claude-sonnet-4-6': {
    input: 3.0,
    output: 15.0,
    cacheWrite: 3.75,
    cacheRead: 0.3,
  },
  'claude-haiku-4-5-20251001': {
    input: 1.0,
    output: 5.0,
    cacheWrite: 1.25,
    cacheRead: 0.1,
  },
  'gemini-2.5-flash': {
    input: 0.3,
    output: 2.5,
    cacheWrite: 0.375,
    cacheRead: 0.03,
  },
  'gpt-4.1-mini': {
    input: 0.4,
    output: 1.6,
    cacheWrite: 0.4,
    cacheRead: 0.1,
  },
  // OpenAI models called through `@edgevanta/ai`'s token-usage accounting. They
  // carry input/output only — that is exactly what the table they came from
  // held, and inventing a cache price to fill the shape would be a fabricated
  // number in a ledger.
  'gpt-5': { input: 10.0, output: 30.0 },
  'gpt-5-mini': { input: 1.0, output: 3.0 },
  'gpt-5-nano': { input: 0.3, output: 0.9 },
  'gpt-4.1': { input: 5.0, output: 15.0 },
  'gpt-4.1-nano': { input: 0.2, output: 0.6 },
  o3: { input: 30.0, output: 120.0 },
  'o3-mini': { input: 6.0, output: 24.0 },
  'gpt-4o': { input: 2.5, output: 10.0 },
  'gpt-4o-mini': { input: 0.15, output: 0.6 },
};

/** The price for a model id, or null when it has no entry. */
export function modelPrice(
  model: string,
  at: Date = new Date(),
): ModelPrice | null {
  if (
    model === 'claude-sonnet-5' &&
    at.getTime() >= SONNET_5_STANDARD_PRICE_AT_MS
  ) {
    return SONNET_5_STANDARD_PRICE;
  }
  return MODEL_PRICING[model] ?? null;
}

/** USD cost of one call, or null when the model has no price entry (records the
 *  tokens without inventing a dollar figure). Rounded to 6 dp — sub-cent reads
 *  (a warm-cache totals read) still carry a meaningful value. */
export function costUsd(options: {
  model: string;
  uncachedInputTokens: number;
  outputTokens: number;
  cacheWriteTokens?: number;
  cacheReadTokens?: number;
  at?: Date;
}): number | null {
  const {
    model,
    uncachedInputTokens,
    outputTokens,
    cacheWriteTokens = 0,
    cacheReadTokens = 0,
    at = new Date(),
  } = options;
  const price = modelPrice(model, at);
  if (!price) {
    return null;
  }
  // A cached call against a model with no published cache price cannot be
  // costed. Say so rather than treating the cache buckets as free.
  if (
    (cacheWriteTokens > 0 && price.cacheWrite === undefined) ||
    (cacheReadTokens > 0 && price.cacheRead === undefined)
  ) {
    return null;
  }
  const usd =
    (uncachedInputTokens / 1_000_000) * price.input +
    (cacheWriteTokens / 1_000_000) * (price.cacheWrite ?? 0) +
    (cacheReadTokens / 1_000_000) * (price.cacheRead ?? 0) +
    (outputTokens / 1_000_000) * price.output;
  return Math.round(usd * 1_000_000) / 1_000_000;
}
